import * as angular from 'angular';
import type { GrabData } from 'go-modules/aria-decorator/aria-ng-sortable.decorator';
import { EventService } from '@go-modules/services/event/event.service';
import { EVENT_NAMES } from '@go-modules/services/event/event-names.constants';

/* @ngInject */
export function RubricScaleController ($scope, $element, $timeout, $translate, eventService: EventService) {
	const MAX_OPTIONS = 10;
	const scaleAriaKeys = {
		numeric: {
			itemTitle: 'rubric_aria-numeric-scale-item-title',
			itemDescription: 'rubric_aria-numeric-scale-item-description',
			title: 'rubric_aria-numeric-scale-title',
			description: 'rubric_aria-numeric-scale-description',
			points: 'rubric_aria-numeric-scale-points'
		},
		qualitative: {
			itemTitle: 'rubric_aria-qualitative-scale-item-title',
			itemDescription: 'rubric_aria-qualitative-scale-item-description',
			title: 'rubric_aria-qualitative-scale-title',
			description: 'rubric_aria-qualitative-scale-description',
			points: 'rubric_aria-qualitative-scale-points'
		}
	};

	const vm = this;

	vm.$onInit = () => {
		$scope.sortableOptions = {
			group: {
				pull: false
			},
			sort: true,
			scroll: true,
			ghostClass: 'rb-element-placeholder',
			handle: `.${$scope.element.type}-option-handle`,
			draggable: '.rb-scale-option-draggable',
			speakerId: 'rubric-ng-sortable-speaker',
			onUpdate () {
				eventService.broadcast(EVENT_NAMES.RUBRIC_DATA_CHANGE);
			}
		};

		updateShowMore();
	};

	function updateShowMore () {
		$scope.showMore = $scope.element.options.length < MAX_OPTIONS;
	}

	function getScaleAriaKey (key) {
		return scaleAriaKeys[$scope.element.type]?.[key];
	}

	$scope.getScaleAriaIndex = function () {
		const scaleIndex = ($scope.schema?.elements || [])
			.filter((item) => item.type === $scope.element.type)
			.indexOf($scope.element);

		return scaleIndex >= 0 ? scaleIndex + 1 : null;
	};

	$scope.getScaleTitleAriaLabel = function () {
		const scaleIndex = $scope.getScaleAriaIndex();
		const fallbackLabel = $translate.instant(getScaleAriaKey('itemTitle'));
		const label = $scope.element.title || fallbackLabel;
		return scaleIndex != null ? `${label} ${scaleIndex}` : label;
	};

	$scope.getScaleDescriptionAriaLabel = function () {
		const scaleIndex = $scope.getScaleAriaIndex();
		const fallbackLabel = $translate.instant(getScaleAriaKey('itemDescription'));
		const label = $scope.element.desc || fallbackLabel;
		return scaleIndex != null ? `${label} ${scaleIndex}` : label;
	};

	$scope.getScaleOptionAriaIndex = function (option) {
		const optionIndex = ($scope.element.options || []).indexOf(option);
		return optionIndex >= 0 ? optionIndex + 1 : null;
	};

	$scope.getScaleOptionAriaSuffix = function (option) {
		const scaleIndex = $scope.getScaleAriaIndex();
		const optionIndex = $scope.getScaleOptionAriaIndex(option);

		if (scaleIndex == null || optionIndex == null) {
			return null;
		}

		return `${scaleIndex} ${optionIndex}`;
	};

	$scope.getScaleOptionTitleAriaLabel = function (option) {
		const optionSuffix = $scope.getScaleOptionAriaSuffix(option);
		const label = option.label || $translate.instant(getScaleAriaKey('title'));
		return optionSuffix != null ? `${label} ${optionSuffix}` : label;
	};

	$scope.getScaleOptionDescriptionAriaLabel = function (option) {
		const optionSuffix = $scope.getScaleOptionAriaSuffix(option);
		const label = option.desc || $translate.instant(getScaleAriaKey('description'));
		return optionSuffix != null ? `${label} ${optionSuffix}` : label;
	};

	$scope.getScaleOptionPointsAriaLabel = function (option) {
		const optionSuffix = $scope.getScaleOptionAriaSuffix(option);
		let label;

		if (option.value || option.value === 0) {
			label = $translate.instant(option.value === 1 ? 'rubric_aria-point-value' : 'rubric_aria-points-value', {value: option.value});
		} else {
			label = $translate.instant(getScaleAriaKey('points'));
		}

		return optionSuffix != null ? `${label} ${optionSuffix}` : label;
	};

	$scope.getTotal = function () {
		let max = 0;
		angular.forEach($scope.element.options, function (option) {
			if (option.value > max) {
				max = Number(option.value);
			}
		});
		return max;
	};

	$scope.select = function (item, option) {
		// because scales don't always have points,
		// we have to remember the id of the option selected
		if (parseInt(item.option, 10) === parseInt(option.id, 10)) {
			item.option = null;
			item.points = null;
		} else {
			item.option = option.id;
			item.points = option.value;
		}

		// emit change event
		eventService.broadcast(EVENT_NAMES.RUBRIC_DATA_CHANGE);
	};

	$scope.addOption = function () {

		const option = {
			id: new Date().getTime(),
			label: '',
			desc: '',
			value: ''
		};

		$scope.element.options.push(option);

		updateShowMore();
		eventService.broadcast(EVENT_NAMES.RUBRIC_DATA_CHANGE);

		$timeout(() => {
			const handles = $element[0].querySelectorAll('.rb-scale-option-list .option-handle');
			const handle = handles[handles.length - 1];
			const ngSortable = angular.element($element[0].querySelector('[ng-sortable]'));
			const additionalText = $translate.instant('rubric-element-scale_added-aria-live-text', {type: $scope.element.type});

			ngSortable.triggerHandler('grabHandle', {handle, speakerAdditionalText: {prepend: additionalText}} as GrabData);
		});
	};

	$scope.removeOption = function (option) {
		angular.forEach($scope.element.options, function (item, key) {
			if (item === option) {
				$scope.element.options.splice(key, 1);
			}
		});
		updateShowMore();

		eventService.broadcast(EVENT_NAMES.RUBRIC_DATA_CHANGE);
	};
}
